import React, { useEffect, useRef, useState } from 'react';

/**
 * The orbit field, live in the page — a faithful port of the launch pack's
 * component, which itself mirrors src/components/waiting/OrbitField.js:
 * angle = phase + omega·t, inner bodies faster (Kepler), TILT squashing the
 * plane, flat discs carrying the day count, accent border once a loop is past
 * its nudge horizon.
 *
 * Holds still under prefers-reduced-motion.
 */
const TILT = 0.6;
const DARK = '#111113';
const DARK_SURFACE = '#1C1C1E';
const INK_DARK = '#F2F2F7';
const MUTED_DARK = '#98989E';
const ACCENT_DARK = '#E8703A';
const MONO = "'IBM Plex Mono',ui-monospace,Menlo,monospace";
const SANS = "-apple-system,BlinkMacSystemFont,'SF Pro Text',sans-serif";

const BODIES = [
  { who: 'Mum', days: 0, r: 88, size: 30, omega: 0.6, phase: 0.6 },
  { who: 'Jake', days: 3, r: 142, size: 40, omega: 0.44, phase: 2.5 },
  { who: 'Daniel', days: 12, r: 210, size: 56, omega: 0.31, phase: 4.4 },
  { who: 'Voss Property', days: 23, r: 292, size: 68, omega: 0.23, phase: 1.35, past: true },
];

const bodyPos = (b, t) => {
  const a = b.phase + b.omega * t;
  return { x: Math.cos(a) * b.r, y: Math.sin(a) * b.r * TILT, depth: Math.sin(a) };
};

export default function OrbitFieldAuto({ height = 520 }) {
  const [t, setT] = useState(0);
  const [box, setBox] = useState({ w: 700, h: height });
  const ref = useRef(null);

  useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    let raf;
    let start = null;
    let live = true;
    const tick = (ts) => {
      if (start === null) start = ts;
      if (live) {
        setT((ts - start) / 1000);
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      live = false;
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const measure = () => {
      if (ref.current) setBox({ w: ref.current.offsetWidth, h: ref.current.offsetHeight });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const zoom = Math.min(1, Math.max(0.42, Math.min(box.w / 780, box.h / 480)));
  const cx = box.w / 2;
  const cy = box.h / 2;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{ position: 'relative', width: '100%', height, overflow: 'hidden', background: DARK }}
    >
      {BODIES.map((b, i) => {
        const r = b.r * zoom;
        return (
          <div
            key={`ring-${b.who}`}
            style={{
              position: 'absolute',
              left: cx - r,
              top: cy - r * TILT,
              width: r * 2,
              height: r * 2 * TILT,
              borderRadius: '50%',
              border: `1px dashed ${i === 3 ? 'rgba(232,112,58,0.4)' : 'rgba(242,242,247,0.14)'}`,
            }}
          />
        );
      })}
      <div
        style={{
          position: 'absolute',
          left: cx,
          top: cy,
          transform: `translate(-50%,-50%) scale(${zoom})`,
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: INK_DARK,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontFamily: MONO,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 1,
            color: DARK,
          }}
        >
          YOU
        </span>
      </div>
      {BODIES.map((b) => {
        const p = bodyPos(b, t);
        const persp = 0.86 + 0.28 * ((p.depth + 1) / 2);
        const x = cx + p.x * zoom;
        const y = cy + p.y * zoom;
        const size = b.size * zoom * persp;
        return (
          <div
            key={b.who}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              zIndex: Math.round(100 + 40 * p.depth),
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: -size / 2,
                top: -size / 2,
                width: size,
                height: size,
                borderRadius: '50%',
                background: DARK_SURFACE,
                border: `${b.past ? 2.2 : 1.4}px solid ${
                  b.past ? ACCENT_DARK : 'rgba(242,242,247,0.3)'
                }`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  fontFamily: MONO,
                  fontWeight: 600,
                  fontSize: size * 0.34,
                  color: b.past ? ACCENT_DARK : INK_DARK,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {b.days}
              </span>
            </div>
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: size / 2 + 10,
                transform: 'translate(-50%,0)',
                fontFamily: SANS,
                fontSize: 15,
                color: MUTED_DARK,
                whiteSpace: 'nowrap',
              }}
            >
              {b.who}
            </div>
          </div>
        );
      })}
    </div>
  );
}
